import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useTranslation } from 'react-i18next';
import { Divider } from 'antd';
import {
  AtsButtonMenu,
  Button,
  BUTTON_TYPES,
  Card,
  RichText,
  Icon,
  Tooltip,
  AtsSkeleton,
  Popup,
  notification,
  NOTIFICATION_TYPES,
} from '_atoms';
import {
  PopupBlacklist,
  PopupCandidateDuplicate,
  PopupFire,
  PopupMarkAsReviewed,
  PopupRemoveBlacklist,
} from '_molecules';
import history from 'helpers/history';
import { ACTIONS, Can, SUBJECTS, UI, useAbility } from 'permission';
import { getCandidateStatusColor } from '_templates/Candidates/utils';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import { STATUSES } from 'constants/user';
import * as actions from 'actions';
import { getCandidateLoading } from 'store/selectors/commonSelectors';
import { getUser } from 'store/selectors/authorizeSelectors';
import { getCandidateJobs } from 'store/selectors/candidateSelectors';
import { ROUTES } from 'router/constants';
import { ReactComponent as Location } from 'dist/icons/location.svg';
import { ReactComponent as Clock } from 'dist/icons/clock.svg';
import { ReactComponent as Plane } from 'dist/icons/airplane.svg';
import { ReactComponent as Business } from 'dist/icons/business.svg';
import type { CandidateData, DuplicateCandidate } from 'store/reducers/candidateReducer';
import type { Uuid } from 'store/reducers/types';
import type { IconType } from '_atoms/Icon';
import { linkModifier } from './utils';
import './style.scss';

interface InfoItemProps {
  ItemIcon: React.FC<{ className?: string }>;
  children?: React.ReactNode;
}

const InfoItem: React.FC<InfoItemProps> = ({ ItemIcon, children }) =>
  children ? (
    <div className="info-item">
      <ItemIcon className="info-item__icon" />
      {children}
    </div>
  ) : null;

interface DeleteCandidatePopupProps {
  open: boolean;
  candidate?: Partial<CandidateData>;
  onClose: () => void;
  onDelete: () => void;
}

export const DeleteCandidatePopup: React.FC<DeleteCandidatePopupProps> = ({
  open,
  candidate = {},
  onClose,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Popup
      open={open}
      title={t('translation:popup_delete_candidate.title')}
      className="delete-candidate-popup"
      cancelButtonProps={{ onClick: onClose, children: t('translation:popup_delete_candidate.button_back') }}
      okButtonProps={{
        onClick: onDelete,
        children: t('translation:popup_delete_candidate.button_delete'),
      }}
    >
      <Typography.Text weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {candidate.first_name_latin} {candidate.last_name_latin}
      </Typography.Text>
      <Typography.Caption className="delete-candidate-popup__local-name">
        {candidate.first_name_local} {candidate.last_name_local}
      </Typography.Caption>
      <Typography.Text className="delete-candidate-popup__warning">
        {t('translation:popup_delete_candidate.info_text')}
      </Typography.Text>
    </Popup>
  );
};

interface CandidateDetailsProps {
  candidateData: CandidateData;
  showAssignPopup: () => void;
  showShare?: boolean;
  isSSC?: boolean;
}

export const CandidateDetails: React.FC<CandidateDetailsProps> = ({
  candidateData,
  showAssignPopup,
  showShare,
  isSSC,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const { id } = useParams<{ id: string }>();
  const user = useSelector(getUser);
  const jobs = useSelector(getCandidateJobs);

  const { first_name_latin, last_name_latin, first_name_local, last_name_local } = candidateData;
  const fullNameEN = (first_name_latin || last_name_latin) && `${first_name_latin} ${last_name_latin}`;
  const fullNameRu = (first_name_local || last_name_local) && `${first_name_local} ${last_name_local}`;
  const [copiedText, setCopiedText] = useState('');
  const emails = useMemo(
    () => candidateData.email.map(link => linkModifier({ type: 'Email', link, linkFull: link }, copiedText)),
    [candidateData.email]
  );
  const phones = useMemo(
    () => candidateData.phone.map(link => linkModifier({ type: 'Phone', link, linkFull: link }, copiedText)),
    [candidateData.phone]
  );
  const socialNetworks = useMemo(
    () => [
      ...candidateData.telegram.map(tg => linkModifier({ type: 'Telegram', link: tg, label: tg })),
      ...candidateData.social_networks.map(n => linkModifier({ type: n.network, link: n.link, label: n.link })),
    ],
    [candidateData.social_networks, candidateData.telegram]
  );
  const links = [...phones, ...emails, ...socialNetworks];
  const { city, state, country, timezone, description, is_ready_to_relocate, is_ready_to_business_trips, status } =
    candidateData;
  const location = `${city ? `${city}, ` : ''}${state && state !== 'Default state' ? `${state}, ` : ''}${
    country || ''
  }`;

  const [openBlacklistPopup, setOpenBlacklistPopup] = useState(false);
  const [openRemoveBlacklistPopup, setOpenRemoveBlacklistPopup] = useState(false);
  const [openFirePopup, setOpenFirePopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openReviewPopup, setOpenReviewPopup] = useState(false);

  const [potentialDuplicates, setPotentialDuplicates] = useState<DuplicateCandidate[]>([]);
  const [duplicatesPopup, setDuplicatesPopup] = useState<Uuid | null | undefined>(null);

  const onDuplicatesPopupClose = () => {
    setDuplicatesPopup(null);
    setPotentialDuplicates([]);
  };

  const getSSCPotentialDuplicates = () => {
    actions.getSSCPotentialDuplicatesAPI(id).then(duplicates => {
      setPotentialDuplicates(duplicates || []);
      if (duplicates?.length) {
        notification({
          message: t('translation:candidate_details.text_found_duplicates', {
            count: duplicates.length,
          } as Record<string, unknown>),
          btn: (
            <span role="none" onClick={() => setDuplicatesPopup(candidateData.uuid)}>
              {t('translation:candidate_details.button_open_duplicates')}
            </span>
          ),
          type: NOTIFICATION_TYPES.WARNING,
        });
      }
    });
  };

  const showPopupBlacklist = () => {
    if (isSSC) getSSCPotentialDuplicates();
    setOpenBlacklistPopup(true);
  };
  const closePopupBlacklist = () => setOpenBlacklistPopup(false);

  const showPopupRemoveBlacklist = () => setOpenRemoveBlacklistPopup(true);
  const closePopupRemoveBlacklist = () => setOpenRemoveBlacklistPopup(false);

  const showPopupFire = () => {
    setOpenFirePopup(true);
  };
  const closePopupFire = () => setOpenFirePopup(false);

  const showPopupDelete = () => {
    setOpenDeletePopup(true);
  };
  const closePopupDelete = () => setOpenDeletePopup(false);

  const onOpenReviewPopup = () => {
    getSSCPotentialDuplicates();
    setOpenReviewPopup(true);
  };

  const onOpenAssignPopup = () => {
    if (isSSC) getSSCPotentialDuplicates();
    showAssignPopup();
  };
  const onCloseReviewPopup = () => setOpenReviewPopup(false);

  const deleteCandidate = () => {
    actions.deleteCandidate(id, isSSC).then(() => {
      const role = user?.active_role?.name;
      const dashboard = `${ROUTES.DASHBOARD}/${role?.toLowerCase().replace(/\s/g, '_')}`;
      if (isSSC) history.push(`${dashboard}?tab=ssc`);
      else history.push(ROUTES.CANDIDATES);
      closePopupDelete();
    });
  };

  const onEditClick = () => {
    if (isSSC) history.push(`/self-submitted-candidates/${id}/edit`);
    else history.push(`/candidates/${candidateData.uuid}/edit`);
  };
  const onReviewed = () => history.push(`/candidates/${candidateData.uuid}/show`);
  const onShareProfile = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(
          `${window.location.origin}/${isSSC ? 'self-submitted-candidates' : 'candidates'}/preview/${
            candidateData.uuid
          }`
        )
        .then(() =>
          notification({
            message: t('translation:candidate_details.text_link_copied'),
            type: NOTIFICATION_TYPES.SUCCESS,
          })
        );
    }
  };
  // const onPrintCv = () => window.print();

  const loading = useSelector(getCandidateLoading);

  const editAction = ability.can(ACTIONS.UPDATE, SUBJECTS.CANDIDATE);
  const shareProfile = ability.can(ACTIONS.READ, UI.SHARE_PROFILE);
  const blacklistAction = ability.can(ACTIONS.READ, subject(UI.BLACKLIST_CANDIDATE, { status }));
  const unBlacklistAction = ability.can(ACTIONS.READ, subject(UI.CANDIDATE_REMOVE_BLACKLIST, { status }));
  const fireAction =
    ability.can(ACTIONS.READ, UI.FIRE_CANDIDATE) && jobs.find(j => j.candidate_status === STATUSES.HIRED);
  const deleteAction = ability.can(ACTIONS.DELETE, SUBJECTS.CANDIDATE);
  const disableAssignCandidate = status === STATUSES.BLACKLISTED || loading;

  const onClickLink = (link: string, type: string, linkFull?: string) => (e: React.MouseEvent) => {
    if (type === 'Phone' || type === 'Email') {
      e.preventDefault();
      setCopiedText(link + type);
      navigator.clipboard?.writeText(linkFull || '');
      setTimeout(() => {
        setCopiedText('');
      }, 3000);
    }
  };

  return (
    <Card className="candidate-details">
      <div className="candidate-details__header">
        {loading ? (
          <AtsSkeleton height={74} className="candidate-name__loader" />
        ) : (
          <div className="candidate-name">
            <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{fullNameEN}</Typography.Title2>
            <Typography.Title4 weight={TYPOGRAPHY_WEIGHT.BOLD}>{fullNameRu}</Typography.Title4>
            <Status type={getCandidateStatusColor(status)}>{status}</Status>
          </div>
        )}
        {showShare ? (
          shareProfile && (
            <div className="candidate-details__buttons">
              <Button style={{ width: '100%' }} type={BUTTON_TYPES.SECONDARY} onClick={onShareProfile}>
                {t('translation:candidate_details.button_share_profile')}
              </Button>
            </div>
          )
        ) : (
          <div className="candidate-details__buttons">
            <Can I={ACTIONS.READ} a={UI.ASSIGN_CANDIDATE}>
              <Button type={BUTTON_TYPES.PRIMARY} onClick={onOpenAssignPopup} disabled={disableAssignCandidate}>
                {t('translation:candidate_details.button_assign_to_job')}
              </Button>
            </Can>
            <AtsButtonMenu disabled={loading}>
              {editAction && (
                <li role="none" onClick={onEditClick}>
                  {t('translation:candidate_details.button_edit_candidate')}
                </li>
              )}
              {isSSC && (
                <li role="none" onClick={onOpenReviewPopup}>
                  {t('translation:candidate_details.button_mark_as_reviewed')}
                </li>
              )}
              {shareProfile && (
                <li role="none" onClick={onShareProfile}>
                  {t('translation:candidate_details.button_share_profile')}
                </li>
              )}
              {fireAction && (
                <li role="none" onClick={showPopupFire}>
                  {t('translation:candidate_details.button_fire_candidate')}
                </li>
              )}
              {blacklistAction && (
                <li role="none" onClick={showPopupBlacklist}>
                  {t('translation:candidate_details.button_blacklist')}
                </li>
              )}
              {unBlacklistAction && (
                <li role="none" onClick={showPopupRemoveBlacklist}>
                  {t('translation:candidate_details.button_remove_blacklist')}
                </li>
              )}
              {deleteAction && (
                <li role="none" className="error" onClick={showPopupDelete}>
                  {t('translation:candidate_details.button_delete')}
                </li>
              )}
            </AtsButtonMenu>
          </div>
        )}
      </div>

      <Divider />

      <div className="candidate-details__block">
        {loading ? (
          <AtsSkeleton height={100} />
        ) : (
          links.map(({ type, link, label, linkFull }) => (
            <a
              key={link + type}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClickLink(link, type, linkFull as string | undefined)}
            >
              <Tooltip label={label} className="candidate-details__link__tooltip">
                <Icon type={type as IconType} className="candidate-details__link" />
              </Tooltip>
            </a>
          ))
        )}
      </div>
      {loading ? (
        <div className="candidate-details__block">
          <AtsSkeleton height={64} />
        </div>
      ) : (
        <div className="candidate-details__block">
          <Typography.Label>{t('translation:candidate_details.label_location')}</Typography.Label>
          <InfoItem ItemIcon={Location}>{location}</InfoItem>
          <InfoItem ItemIcon={Clock}>{timezone}</InfoItem>
          {!(location && timezone) && <Typography.Text>-</Typography.Text>}
        </div>
      )}
      {loading ? (
        <div className="candidate-details__block">
          <AtsSkeleton height={64} />
        </div>
      ) : (
        <div className="candidate-details__block">
          <Typography.Label>{t('translation:candidate_details.label_relocation')}</Typography.Label>
          <InfoItem ItemIcon={Plane}>
            {is_ready_to_relocate && t('translation:candidate_details.text_ready_to_relocate')}
          </InfoItem>
          <InfoItem ItemIcon={Business}>
            {is_ready_to_business_trips && t('translation:candidate_details.text_ready_for_business_trips')}
          </InfoItem>
          {!(is_ready_to_relocate && is_ready_to_business_trips) && <Typography.Text>-</Typography.Text>}
        </div>
      )}
      {loading ? (
        <div className="candidate-details__block">
          <AtsSkeleton height={32} />
        </div>
      ) : (
        <div className="candidate-details__block">
          <Typography.Label>{t('translation:candidate_details.label_description')}</Typography.Label>
          <RichText text={description || '-'} />
        </div>
      )}
      <PopupBlacklist candidate={candidateData} open={openBlacklistPopup} onClose={closePopupBlacklist} isSSC={isSSC} />
      <PopupRemoveBlacklist
        candidate={candidateData}
        open={openRemoveBlacklistPopup}
        onClose={closePopupRemoveBlacklist}
      />
      <PopupFire open={openFirePopup} onClose={closePopupFire} />
      <DeleteCandidatePopup
        open={openDeletePopup}
        candidate={candidateData}
        onClose={closePopupDelete}
        onDelete={deleteCandidate}
      />
      <PopupMarkAsReviewed
        open={!!openReviewPopup}
        onClose={onCloseReviewPopup}
        candidateUuid={id}
        candidateName={`${candidateData.first_name_latin} ${candidateData.last_name_latin}`}
        onReviewed={onReviewed}
      />
      <PopupCandidateDuplicate
        open={!!duplicatesPopup}
        candidateDuplicates={potentialDuplicates as never}
        duplicate={duplicatesPopup as never}
        onClose={onDuplicatesPopupClose}
        loading={!!duplicatesPopup && !potentialDuplicates.length}
        isMergeAble={isSSC}
      />
    </Card>
  );
};
